import { Stack } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useIdentification } from 'hook/query/useIdentification'
import { useWebSocket } from 'hook/useWebsocket'
import { ShiftOptions } from 'models/ICattlesData'
import { useEffect, useMemo, useState } from 'react'
import { COLORS } from 'styles/colors'
import { CardStandart } from 'ui/CardStandart'
import CustomPagination from 'ui/CustomPagination'
import Preloader from 'ui/Preloader'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { IdentificationState } from '../../DigitizationPage'
import { CardIdentification } from '../card-identification/CardIdentification'

interface TableBodyProps {
	state: IdentificationState
	cameraId: number
	isRealIdent: boolean
	setState: React.Dispatch<
		React.SetStateAction<{
			gate: number[]
			date: number
			shift: ShiftOptions
			isDirty: boolean
		}>
	>
}
export const PAGE_SIZE = 25

export const TableBody = (props: TableBodyProps) => {
	const { state, cameraId, isRealIdent } = props
	const [page, setPage] = useState<number>(1)
	const params = {
		shift: state.shift,
		gate: state.gate[0],
		date: state.date,
		cameraId: cameraId,
		page: isRealIdent ? 1 : page,
		pageSize: PAGE_SIZE,
		isEnabled: !props.isRealIdent,
	}
	const queryKey = useMemo(() => {
		return {
			rec_date: state.date,
			camera_id: cameraId,
			day_period: state.shift,
			page: isRealIdent ? 1 : page,
			page_size: PAGE_SIZE,
		}
	}, [state.date, state.shift, cameraId, isRealIdent])
	const queryClient = useQueryClient()

	const { data, isLoading, error } = useIdentification(params)

	const { latestMessage } = useWebSocket(state.gate[0], isRealIdent)

	useEffect(() => {
		if (!latestMessage || latestMessage.type !== 'new_tracking') return

		queryClient.setQueryData<any>(['identificationList', queryKey], oldData => {
			const prevResults = oldData?.results ?? []
			return {
				...oldData,
				results: [latestMessage.payload, ...prevResults],
			}
		})
	}, [latestMessage, queryKey, queryClient])

	const pagedResults = useMemo(() => {
		if (!data?.results) return []

		if (isRealIdent) {
			const start = (page - 1) * PAGE_SIZE
			return data.results.slice(start, start + PAGE_SIZE)
		}

		return data.results
	}, [data?.results, page, isRealIdent])
	return (
		<Stack direction={'column'} gap={'24px'} minHeight={'500px'}>
			{isLoading ? (
				<Preloader />
			) : isRealIdent && !data?.results ? (
				<Stack
					sx={{ flex: 1 }}
					direction={'column'}
					justifyContent={'center'}
					alignItems={'center'}
				>
					<TypographySecondary14>
						{state.gate[0]
							? 'Для старта оцифровки необходимо запустить корову на дойку'
							: 'Выберите вход, в котором нужно провести оцифровку'}
					</TypographySecondary14>
				</Stack>
			) : error?.response?.data ||
			  (!isRealIdent &&
					data?.results &&
					data.results.some(item => 'day_period' in item)) ? (
				<Stack
					sx={{ flex: 1 }}
					direction={'column'}
					justifyContent={'center'}
					alignItems={'center'}
				>
					<TypographySecondary14>
						Выберите смену и дату, за которую хотите провести оцифровку
					</TypographySecondary14>
				</Stack>
			) : data?.results.length === 0 ? (
				<Stack
					sx={{ flex: 1 }}
					direction={'column'}
					justifyContent={'center'}
					alignItems={'center'}
				>
					<TypographySecondary14>
						В этот период не было оцифрованных коров.
					</TypographySecondary14>
				</Stack>
			) : (
				<Stack
					direction={'column'}
					gap={'24px'}
					minHeight={'460px'}
					sx={{
						overflowY: 'auto',
						overflowX: 'hidden',
						maxHeight: '600px',
						'&::-webkit-scrollbar': {
							width: 4,
						},
						'&::-webkit-scrollbar-track': {
							backgroundColor: '#DBDBDB',
							borderRadius: 12,
						},
						'&::-webkit-scrollbar-thumb': {
							backgroundColor: COLORS.icon,
							borderRadius: 12,
						},
					}}
				>
					{pagedResults?.map((el, index) => (
						<CardIdentification
							key={el.id}
							{...state}
							{...el}
							setState={props.setState}
							queryKey={queryKey}
							index={(page - 1) * PAGE_SIZE + index + 1}
						/>
					))}
				</Stack>
			)}

			<CardStandart sx={{ padding: '24px' }}>
				<CustomPagination
					onChangeFromServer={setPage}
					count={PAGE_SIZE}
					totalCount={isRealIdent ? data?.results?.length : data?.count}
				/>
			</CardStandart>
		</Stack>
	)
}
